import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { dropPrerenderedJsonLd, injectJsonLd } from "@/lib/jsonld";

const SITE = "https://procuregroup.uk";

/**
 * Sitewide machine-readable description of the business, for search engines and AI assistants.
 * Everything here comes from the company settings, so it cannot drift from what the site says.
 *
 * There is ONE company entity, `/#organisation`, and the rating is stated once — on the homepage,
 * which is the page that displays "4.9, based on 20 Google reviews" to visitors. If the
 * pre-rendered HTML already carries this entity, nothing is added on top of it.
 */
export default function SiteSchema() {
  const [settings, setSettings] = useState(null);
  const location = useLocation();
  const entryPath = useRef(location.pathname);

  useEffect(() => {
    api.get("/site/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  // The pre-rendered nodes describe the URL the browser loaded. Once the visitor moves on, they
  // are stale, so they go and this component's own nodes take over.
  useEffect(() => {
    if (location.pathname !== entryPath.current) dropPrerenderedJsonLd();
  }, [location.pathname]);

  useEffect(() => {
    if (!settings) return;
    const rating = Number(settings.google_rating || 0);
    const count = Number(settings.google_review_count || 0);
    const onHomepage = location.pathname === "/";
    const blocks = [
      {
        "@context": "https://schema.org",
        "@type": ["Organization", "HomeAndConstructionBusiness"],
        "@id": `${SITE}/#organisation`,
        name: settings.company_name || "Procure Group Ltd",
        alternateName: settings.trading_name || undefined,
        url: SITE,
        logo: `${SITE}/procure-logo.png`,
        image: `${SITE}/procure-logo.png`,
        description: settings.positioning
          || "Damp, mould, timber and below-ground damp protection specialists covering the East of England and South Midlands.",
        telephone: settings.telephone,
        email: settings.email,
        vatID: settings.vat_number || undefined,
        taxID: settings.company_number || undefined,
        priceRange: "££",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Bellingham House, 2 Huntington Road",
          addressLocality: "St Neots",
          addressRegion: "Cambridgeshire",
          postalCode: "PE19 1BG",
          addressCountry: "GB",
        },
        openingHoursSpecification: settings.opening_hours_schema?.length
          ? settings.opening_hours_schema : undefined,
        areaServed: [
          ...["Bedfordshire", "Cambridgeshire", "Hertfordshire", "Northamptonshire"]
            .map((name) => ({ "@type": "AdministrativeArea", name })),
          ...["Milton Keynes", "Peterborough"].map((name) => ({ "@type": "City", name })),
        ],
        contactPoint: [{
          "@type": "ContactPoint",
          contactType: "customer service",
          telephone: settings.telephone,
          email: settings.email,
          areaServed: "GB",
          availableLanguage: "en-GB",
        }],
        knowsAbout: ["Rising damp", "Penetrating damp", "Condensation and mould",
                     "Timber decay and woodworm", "Below-ground damp protection",
                     "Damp-resistant replastering"],
        sameAs: [settings.google_review_url].filter(Boolean),
        ...(onHomepage && rating > 0 && count > 0 ? {
          aggregateRating: {
            "@type": "AggregateRating", ratingValue: rating.toFixed(1),
            reviewCount: count, bestRating: "5",
          },
        } : {}),
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": `${SITE}/#website`,
        url: SITE,
        name: settings.company_name || "Procure Group Ltd",
        publisher: { "@id": `${SITE}/#organisation` },
        inLanguage: "en-GB",
      },
    ];

    const scripts = injectJsonLd(blocks, "data-pg-site-jsonld");
    return () => scripts.forEach((script) => script.remove());
  }, [settings, location.pathname]);

  return null;
}
